/**
 * `/reset-password` — password recovery flow.
 *
 * auth-flow-hardening Inc-1 (2026-05-20): rewritten to fix the
 * "auth error on submit" bug that shipped before this version.
 *
 * ## The bug this fixes
 *
 * Pre-fix the page rendered the form on mount + fired
 * `supabase.auth.updateUser({password})` on submit WITHOUT ever
 * establishing the recovery session. Supabase's PKCE flow (the
 * Arcanum default per `lib/supabase.ts`) requires either:
 *   (a) an `onAuthStateChange` listener catching `PASSWORD_RECOVERY` —
 *       the documented Supabase pattern, OR
 *   (b) an explicit `verifyOtp({type:'recovery', token_hash})` call
 *       when the URL carries `?token_hash=...` (newer PKCE shape)
 *
 * Neither was wired. `updateUser` 401d with `AuthSessionMissingError`
 * and the raw message rendered as "auth error" to the user.
 *
 * ## The fix
 *
 * 1. Mount-effect subscribes to `onAuthStateChange` and flips
 *    `recoverySessionReady` on receiving `PASSWORD_RECOVERY` (Supabase's
 *    canonical event when a recovery-link session lands).
 * 2. On mount, ALSO check for `?token_hash=...` in the query string
 *    (the newer PKCE shape Supabase 2.x defaults to) and explicitly
 *    `verifyOtp({type:'recovery', token_hash})` — this fires
 *    `PASSWORD_RECOVERY` via the listener, closing the loop.
 * 3. If no recovery session establishes within 3 seconds, render a
 *    "Link expired" UI with a CTA back to `/forgot-password`.
 * 4. Form is GATED on `recoverySessionReady` — the user can't submit
 *    a password update before the session exists.
 * 5. On successful `updateUser`, explicit `signOut()` then redirect
 *    to `/login` so the user signs in fresh with the new password
 *    (per product decision 2026-05-20: cleaner UX than auto-upgrading
 *    the recovery session into a full session and bouncing to /cockpit).
 *
 * ## URL shapes this handles
 *
 * - **PKCE recovery (Supabase 2.x default, most likely shape today)**:
 *   `https://app/reset-password?token_hash=<hash>&type=recovery`
 *   → mount effect calls `verifyOtp({type:'recovery', token_hash})`
 *   → fires `PASSWORD_RECOVERY` event → ready=true.
 *
 * - **Legacy implicit recovery (older Supabase projects)**:
 *   `https://app/reset-password#access_token=<jwt>&type=recovery`
 *   → `detectSessionInUrl: true` (lib/supabase.ts) auto-extracts the
 *   hash → fires `PASSWORD_RECOVERY` via the listener.
 *
 * - **Direct navigation (no recovery link)**:
 *   `https://app/reset-password` (no query, no hash)
 *   → no `PASSWORD_RECOVERY` ever fires → 3s timeout → "Link expired"
 *   UI prompts the user to request a new link.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';
import { AuthLogo } from '@/components/layout/AuthLogo';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button } from '@/components/ui/Button';

/** Wall-clock budget for the recovery session to land before we give up. */
const RECOVERY_TIMEOUT_MS = 3000;

type RecoveryState =
  | 'waiting' // mount → listener armed → polling for PASSWORD_RECOVERY
  | 'ready' // recovery session established, form interactive
  | 'expired' // 3s elapsed without PASSWORD_RECOVERY, "request new link" UI
  | 'submitted'; // user submitted, password updated, redirecting

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recoveryState, setRecoveryState] = useState<RecoveryState>('waiting');

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Step 1 — subscribe to PASSWORD_RECOVERY before kicking off the
    // verifyOtp call so we don't miss the event.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY') {
        if (timeoutId) clearTimeout(timeoutId);
        setRecoveryState('ready');
      }
    });

    // Step 2 — for the PKCE recovery shape (?token_hash=...&type=recovery),
    // explicitly verify the OTP. The hash-based legacy shape is
    // auto-handled by `detectSessionInUrl: true` (lib/supabase.ts).
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type');
    if (tokenHash && type === 'recovery') {
      void supabase.auth
        .verifyOtp({ type: 'recovery', token_hash: tokenHash })
        .then(({ error: verifyErr }) => {
          if (cancelled) return;
          if (verifyErr) {
            // verifyOtp failure shapes: expired link, already-consumed,
            // invalid token_hash. All collapse to "link expired" UX.
            if (timeoutId) clearTimeout(timeoutId);
            setRecoveryState('expired');
          }
          // Success path is handled by the PASSWORD_RECOVERY event above.
        });
    }

    // Step 3 — fallback timeout. If neither path establishes a recovery
    // session within RECOVERY_TIMEOUT_MS, show the "Link expired" UI.
    // Cleared by the PASSWORD_RECOVERY handler above if recovery lands first.
    timeoutId = setTimeout(() => {
      if (cancelled) return;
      // Defensive: check the actual session before flipping to expired,
      // in case the listener missed the event (race on initial mount).
      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        if (session) {
          setRecoveryState('ready');
        } else {
          setRecoveryState('expired');
        }
      });
    }, RECOVERY_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (recoveryState !== 'ready') {
      setError('Recovery session not ready — request a new link.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) {
      setLoading(false);
      setError(err.message);
      return;
    }

    // Per product decision 2026-05-20: sign out the recovery-tier session
    // and route to /login so the user signs in fresh with the new password.
    // Cleaner UX than auto-upgrading + bouncing to /cockpit — gives the user
    // a "confirm your new password works" moment + clears any recovery-scope
    // session state from localStorage.
    setRecoveryState('submitted');
    await supabase.auth.signOut();
    navigate({
      to: '/login',
      search: { reset: 'success' as const },
    });
  };

  if (recoveryState === 'expired') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-void">
        <div className="w-full max-w-sm px-8 py-10">
          <AuthLogo />
          <p className="text-sm text-text-muted font-mono text-center mb-6">
            Set your password
          </p>
          <div className="flex flex-col gap-4">
            <div className="px-3 py-3 bg-warn/[0.06] border border-warn/15 rounded-[3px]">
              <p className="text-warn font-mono text-sm">
                This reset link has expired or is invalid. Request a new one to
                continue.
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => navigate({ to: '/forgot-password' })}
            >
              Request a new link
            </Button>
            <button
              type="button"
              onClick={() => navigate({ to: '/login' })}
              className="text-text-muted font-mono text-xs hover:text-text-bright transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-void">
      <div className="w-full max-w-sm px-8 py-10">
        <AuthLogo />
        <p className="text-sm text-text-muted font-mono text-center mb-6">
          Set your password
        </p>

        {recoveryState === 'waiting' && (
          <div className="mb-4 px-3 py-2.5 bg-input-bg border border-input-border rounded-[3px]">
            <p className="text-text-muted font-mono text-xs">
              Verifying recovery link…
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
              PASSWORD
            </label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              disabled={recoveryState !== 'ready'}
              aria-label="New password"
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
              CONFIRM PASSWORD
            </label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              disabled={recoveryState !== 'ready'}
              aria-label="Confirm new password"
            />
          </div>

          {error && (
            <div className="px-3 py-2.5 bg-danger/[0.06] border border-danger/13 rounded-[3px]">
              <p className="text-danger font-mono text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || recoveryState !== 'ready'}
            data-testid="reset-password-submit"
            className="w-full py-3.5 mt-1 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold tracking-[0.5px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent/13 hover:border-accent/27 transition-all duration-300"
          >
            {loading ? 'Setting password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
